import React, { useState } from 'react';
import {
    Box,
    Typography,
    Chip,
    alpha,
    useTheme,
    IconButton,
    Tooltip,
} from '@mui/material';
import {
    Whatshot as FlareIcon,
    TrendingUp as TrendingIcon,
    OpenInNew as OpenInNewIcon,
    Create as CreateIcon,
    PlayCircle as PlayCircleIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';

function timeAgo(dateStr) {
    if (!dateStr) return '';
    try {
        const sec = Math.floor((Date.now() - new Date(dateStr)) / 1000);
        if (sec < 60)    return `${sec}s ago`;
        if (sec < 3600)  return `${Math.floor(sec / 60)}m ago`;
        if (sec < 86400) return `${Math.floor(sec / 3600)}h ago`;
        return `${Math.floor(sec / 86400)}d ago`;
    } catch { return ''; }
}

const TrendRow = ({ item, index, accent }) => {
    const theme    = useTheme();
    const navigate = useNavigate();
    const isVideo  = item.source_label?.toLowerCase().includes('youtube') || !!item.video_id;
    const [imgFail, setImgFail] = useState(false);

    const handleCreatePost = (e) => {
        e.preventDefault();
        const hashtags = (item.hashtags || []).join(' ');
        navigate('/create', { state: { prefill: { caption: `${item.topic}\n\n${hashtags}` } } });
    };

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                p: 1.75,
                borderRadius: '14px',
                border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
                bgcolor: theme.palette.mode === 'dark'
                    ? alpha(theme.palette.background.paper, 0.5)
                    : '#ffffff',
                transition: 'all 0.2s ease',
                '&:hover': {
                    borderColor: alpha(accent, 0.3),
                    boxShadow: `0 4px 16px ${alpha(accent, 0.1)}`,
                    bgcolor: alpha(accent, 0.02),
                    '& .row-actions': { opacity: 1 },
                },
            }}
        >
            {/* Rank */}
            <Box sx={{
                width: 32, height: 32, flexShrink: 0,
                borderRadius: '9px',
                bgcolor: index < 3 ? accent : alpha(theme.palette.text.primary, 0.05),
                color: index < 3 ? '#fff' : theme.palette.text.secondary,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '0.8rem', fontWeight: 900,
            }}>
                {index + 1}
            </Box>

            {/* Thumbnail (small) */}
            {item.picture_url && !imgFail ? (
                <Box
                    component="img"
                    src={item.picture_url}
                    alt={item.topic}
                    onError={() => setImgFail(true)}
                    sx={{
                        width: 56, height: 40, borderRadius: '8px',
                        objectFit: 'cover', flexShrink: 0,
                    }}
                />
            ) : (
                <Box sx={{
                    width: 56, height: 40, borderRadius: '8px',
                    bgcolor: alpha(accent, 0.08),
                    flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                    {isVideo
                        ? <PlayCircleIcon sx={{ fontSize: 20, color: alpha(accent, 0.5) }} />
                        : <FlareIcon      sx={{ fontSize: 18, color: alpha(accent, 0.4) }} />
                    }
                </Box>
            )}

            {/* Topic + meta */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    component="a"
                    href={item.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                        fontWeight: 700, fontSize: '0.9rem',
                        color: 'text.primary', textDecoration: 'none',
                        display: '-webkit-box',
                        WebkitLineClamp: 1, WebkitBoxOrient: 'vertical', overflow: 'hidden',
                        '&:hover': { color: accent },
                    }}
                >
                    {item.topic}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: 0.4, flexWrap: 'wrap' }}>
                    {item.source_label && (
                        <Typography sx={{ fontSize: '0.65rem', fontWeight: 700, color: alpha(accent, 0.9) }}>
                            {item.source_label}
                        </Typography>
                    )}
                    {item.source && item.source !== item.source_label && (
                        <Typography sx={{ fontSize: '0.62rem', color: 'text.disabled' }}>
                            via {item.source}
                        </Typography>
                    )}
                    {item.published && (
                        <Typography sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
                            · {timeAgo(item.published)}
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Traffic badge */}
            {item.traffic && (
                <Chip
                    icon={<TrendingIcon sx={{ fontSize: '12px !important', color: `${accent} !important` }} />}
                    label={item.traffic}
                    size="small"
                    sx={{
                        height: 22, fontSize: '0.65rem', fontWeight: 800,
                        bgcolor: alpha(accent, 0.1), color: accent, border: 'none',
                        flexShrink: 0,
                    }}
                />
            )}

            {/* Hashtags (desktop) */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.5, flexShrink: 0 }}>
                {(item.hashtags || []).slice(0, 2).map((tag) => (
                    <Typography key={tag} sx={{ fontSize: '0.62rem', fontWeight: 700, color: alpha(accent, 0.7) }}>
                        {tag}
                    </Typography>
                ))}
            </Box>

            {/* Actions */}
            <Box className="row-actions" sx={{ display: 'flex', gap: 0.5, flexShrink: 0, opacity: 0, transition: 'opacity 0.2s' }}>
                <Tooltip title="Create post from this trend">
                    <IconButton size="small" onClick={handleCreatePost}
                        sx={{ bgcolor: accent, color: '#fff', width: 28, height: 28, '&:hover': { filter: 'brightness(1.1)' } }}
                    >
                        <CreateIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Open source">
                    <IconButton size="small" component="a" href={item.link} target="_blank" rel="noopener noreferrer"
                        sx={{ bgcolor: alpha(theme.palette.divider, 0.08), color: 'text.secondary', width: 28, height: 28 }}
                    >
                        <OpenInNewIcon sx={{ fontSize: 13 }} />
                    </IconButton>
                </Tooltip>
            </Box>
        </Box>
    );
};

export default TrendRow;
